"use client"
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  Copy,
  EllipsisVertical,
  Link as LinkIcon,
} from "lucide-react"
import {
  useEffect,
  useState,
  type ComponentProps,
  type MouseEvent,
  type ReactNode,
} from "react"
import { HighlightedText } from "./highlighted-text"
import { StatusBadge, type StatusBadgeProps } from "../status-badge"
import {
  typographyVariantClassNames,
  wrapperTypographyClassNames,
} from "../../typography"
import { Badge, type BadgeProps } from "./badge"
import { Button } from "./button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../primitives/dropdown-menu"
import { Switch } from "../primitives/switch"
import {
  Table as BaseTable,
  TableBody as BaseTableBody,
  TableCaption as BaseTableCaption,
  TableCell as BaseTableCell,
  TableFooter as BaseTableFooter,
  TableHead as BaseTableHead,
  TableHeader as BaseTableHeader,
  TableRow as BaseTableRow,
} from "../primitives/table"
import { cn } from "../../lib/utils"

type TableAlignment = "left" | "center" | "right"
type TableSortDirection = "asc" | "desc" | null

const tableTextClassNames = {
  description: cn(
    "text-muted-foreground",
    typographyVariantClassNames["text-xs-normal"]
  ),
  header: cn(
    "text-muted-foreground",
    typographyVariantClassNames["text-sm-normal"]
  ),
  stackedLabel: cn(
    "text-muted-foreground",
    typographyVariantClassNames["text-xs-uppercase-medium"]
  ),
  stack: "flex min-w-0 flex-col gap-0.5",
  value: typographyVariantClassNames["text-sm-normal"],
  valueEmphasis: typographyVariantClassNames["text-sm-medium"],
  valueLink: cn(
    "rounded-sm text-foreground hover:underline focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:outline-none",
    typographyVariantClassNames["text-sm-medium"]
  ),
} as const

function getAlignmentClasses(align: TableAlignment = "left") {
  if (align === "center") {
    return {
      cell: "text-center",
      justify: "justify-center",
    }
  }

  if (align === "right") {
    return {
      cell: "text-right",
      justify: "justify-end",
    }
  }

  return {
    cell: "text-left",
    justify: "justify-start",
  }
}

function getSortIcon(sortDirection: TableSortDirection) {
  if (sortDirection === "asc") {
    return (
      <ArrowUp
        aria-hidden="true"
        className="h-3.5 w-3.5 shrink-0 transition-colors"
      />
    )
  }

  if (sortDirection === "desc") {
    return (
      <ArrowDown
        aria-hidden="true"
        className="h-3.5 w-3.5 shrink-0 transition-colors"
      />
    )
  }

  return (
    <ArrowUpDown
      aria-hidden="true"
      className="h-3.5 w-3.5 shrink-0 opacity-70 transition-colors"
    />
  )
}

function getAriaSort(sortDirection: TableSortDirection) {
  if (sortDirection === "asc") return "ascending"
  if (sortDirection === "desc") return "descending"
  return "none"
}

function isPlaceholderText(value?: string | null) {
  if (!value) {
    return true
  }

  const normalized = value.trim().toLowerCase()
  return normalized === "-" || normalized === "—" || normalized === "n/a"
}

type SharedTableProps = ComponentProps<typeof BaseTable>

export function Table({ className, ...props }: SharedTableProps) {
  return (
    <BaseTable
      className={cn(
        "w-full caption-bottom text-sm [&_td]:align-middle [&_th]:align-middle",
        className
      )}
      {...props}
    />
  )
}

export function TableHeader({
  className,
  ...props
}: ComponentProps<typeof BaseTableHeader>) {
  return (
    <BaseTableHeader
      className={cn("[&_tr:hover]:bg-transparent", className)}
      {...props}
    />
  )
}

export function TableBody({
  className,
  ...props
}: ComponentProps<typeof BaseTableBody>) {
  return (
    <BaseTableBody
      className={cn("[&_tr:nth-child(even)]:bg-muted/30", className)}
      {...props}
    />
  )
}

export function TableFooter({
  className,
  ...props
}: ComponentProps<typeof BaseTableFooter>) {
  return <BaseTableFooter className={className} {...props} />
}

export function TableRow({
  className,
  ...props
}: ComponentProps<typeof BaseTableRow>) {
  return (
    <BaseTableRow
      className={cn(
        "hover:bg-muted/40 data-[state=selected]:bg-muted/50",
        className
      )}
      {...props}
    />
  )
}

export function TableHead({
  className,
  ...props
}: ComponentProps<typeof BaseTableHead>) {
  return (
    <BaseTableHead
      className={cn(
        "h-9 px-4 py-2 whitespace-nowrap",
        tableTextClassNames.header,
        className
      )}
      {...props}
    />
  )
}

export function TableCell({
  className,
  ...props
}: ComponentProps<typeof BaseTableCell>) {
  return <BaseTableCell className={cn("px-4 py-3", className)} {...props} />
}

export function TableCaption({
  className,
  ...props
}: ComponentProps<typeof BaseTableCaption>) {
  return <BaseTableCaption className={className} {...props} />
}

export type TableHeaderCellProps = Omit<
  ComponentProps<typeof TableHead>,
  "children"
> & {
  align?: TableAlignment
  label: ReactNode
  onSort?: () => void
  sortDirection?: TableSortDirection
}

export function TableHeaderCell({
  align = "left",
  className,
  label,
  onSort,
  sortDirection = null,
  ...props
}: TableHeaderCellProps) {
  const { onClick: onClickProp, ...restProps } = props
  const alignment = getAlignmentClasses(align)
  const isSortable = Boolean(onSort)
  const isSorted = sortDirection === "asc" || sortDirection === "desc"
  const shouldShowSortIndicator = isSortable || isSorted
  const handleSort = () => {
    onSort?.()
  }

  return (
    <TableHead
      className={cn(alignment.cell, className)}
      aria-sort={
        shouldShowSortIndicator ? getAriaSort(sortDirection) : undefined
      }
      onClick={isSortable ? handleSort : onClickProp}
      {...restProps}
    >
      {isSortable ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            handleSort()
          }}
          className={cn(
            "flex w-full items-center gap-1 rounded-sm transition-colors focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:outline-none",
            isSorted
              ? "text-white"
              : "text-muted-foreground hover:text-foreground",
            alignment.justify
          )}
        >
          <span className="truncate">{label}</span>
          {getSortIcon(sortDirection)}
        </button>
      ) : (
        <span
          className={cn(
            "flex w-full items-center gap-1.5",
            isSorted && "text-white",
            alignment.justify
          )}
        >
          <span className="truncate">{label}</span>
          {isSorted ? getSortIcon(sortDirection) : null}
        </span>
      )}
    </TableHead>
  )
}

export type TableCellTextProps = {
  className?: string
  content?: string | null
  description?: string | null
  descriptionClassName?: string
  highlight?: string
  valueClassName?: string
  weight?: "normal" | "medium"
}

export function TableCellText({
  className,
  content,
  description,
  descriptionClassName,
  highlight,
  valueClassName,
  weight = "normal",
}: TableCellTextProps) {
  const resolvedContent = content?.trim() ? content : null

  return (
    <div className={cn(tableTextClassNames.stack, className)}>
      {resolvedContent ? (
        <HighlightedText
          text={resolvedContent}
          highlight={highlight}
          className={cn(
            "block break-words whitespace-pre-wrap",
            weight === "medium"
              ? tableTextClassNames.valueEmphasis
              : tableTextClassNames.value,
            isPlaceholderText(resolvedContent) && "text-muted-foreground",
            valueClassName
          )}
        />
      ) : null}
      {description ? (
        <HighlightedText
          text={description}
          highlight={highlight}
          className={cn(
            "block break-words",
            tableTextClassNames.description,
            descriptionClassName
          )}
        />
      ) : null}
    </div>
  )
}

export type TableCellTextLinkProps = TableCellTextProps & {
  href: string
  target?: string
}

export function TableCellTextLink({
  className,
  content,
  description,
  descriptionClassName,
  highlight,
  href,
  target,
  valueClassName,
  weight = "normal",
}: TableCellTextLinkProps) {
  return (
    <a
      href={href}
      target={target}
      rel={target === "_blank" ? "noopener noreferrer" : undefined}
      className={cn(
        "group block max-w-full rounded-sm text-foreground focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:outline-none",
        className
      )}
    >
      <TableCellText
        content={content}
        description={description}
        descriptionClassName={descriptionClassName}
        highlight={highlight}
        valueClassName={cn("group-hover:underline", valueClassName)}
        weight={weight}
      />
    </a>
  )
}

export type TableCellLinkProps = {
  content: string
  description?: string | null
  highlight?: string
  href: string
  target?: string
}

export function TableCellLink({
  content,
  description,
  highlight,
  href,
  target,
}: TableCellLinkProps) {
  return (
    <div className="flex flex-col gap-0.5">
      <a
        href={href}
        target={target}
        rel={target === "_blank" ? "noopener noreferrer" : undefined}
        className={cn(
          "group inline-flex max-w-full items-center gap-1 text-foreground focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:outline-none",
          tableTextClassNames.value
        )}
      >
        <LinkIcon
          aria-hidden="true"
          className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70"
        />
        <HighlightedText
          text={content}
          highlight={highlight}
          className="truncate group-hover:underline"
        />
      </a>
      {description ? (
        <HighlightedText
          text={description}
          highlight={highlight}
          className={cn("block break-words", tableTextClassNames.description)}
        />
      ) : null}
    </div>
  )
}

export type TableCellCodeProps = {
  className?: string
  content: string
  copyValue?: string | null
  description?: string | null
  highlight?: string
  href?: string
  target?: string
}

export function TableCellCode({
  className,
  content,
  copyValue,
  description,
  highlight,
  href,
  target,
}: TableCellCodeProps) {
  const [copied, setCopied] = useState(false)
  const valueToCopy = (copyValue ?? content).trim()

  useEffect(() => {
    if (!copied) {
      return undefined
    }

    const timer = window.setTimeout(() => setCopied(false), 1200)
    return () => window.clearTimeout(timer)
  }, [copied])

  const canCopy = valueToCopy.length > 0

  const handleCopy = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (!canCopy || !navigator.clipboard?.writeText) {
      return
    }

    try {
      await navigator.clipboard.writeText(valueToCopy)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="flex max-w-full flex-col gap-0.5">
      <div
        className={cn(
          "inline-flex max-w-full min-w-0 items-center gap-1 overflow-hidden rounded-sm bg-muted px-2 py-1",
          className
        )}
      >
        {href ? (
          <a
            href={href}
            target={target}
            rel={target === "_blank" ? "noopener noreferrer" : undefined}
            className="min-w-0 flex-1 rounded-sm text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:outline-none"
          >
            <HighlightedText
              text={content}
              highlight={highlight}
              className={cn(
                "block min-w-0 truncate font-mono text-muted-foreground",
                typographyVariantClassNames["text-xs-normal"]
              )}
            />
          </a>
        ) : (
          <HighlightedText
            text={content}
            highlight={highlight}
            className={cn(
              "block min-w-0 flex-1 truncate font-mono text-muted-foreground",
              typographyVariantClassNames["text-xs-normal"]
            )}
          />
        )}
        {canCopy ? (
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex size-3 shrink-0 items-center justify-center rounded-sm text-muted-foreground/80 transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:outline-none"
            aria-label={copied ? "Copied" : "Copy code value"}
            title={copied ? "Copied" : "Copy code value"}
          >
            {copied ? (
              <Check aria-hidden="true" className="size-3" />
            ) : (
              <Copy aria-hidden="true" className="size-3" />
            )}
          </button>
        ) : null}
      </div>
      {description ? (
        <HighlightedText
          text={description}
          highlight={highlight}
          className={cn("block break-words", tableTextClassNames.description)}
        />
      ) : null}
    </div>
  )
}

type TableCellBadge = BadgeProps | StatusBadgeProps

function isStatusBadge(badge: TableCellBadge): badge is StatusBadgeProps {
  return "status" in badge
}

export type TableCellBadgesProps = {
  badges: TableCellBadge[]
  description?: string | null
}

export function TableCellBadges({ badges, description }: TableCellBadgesProps) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex flex-wrap items-center gap-1">
        {badges.map((badge, index) =>
          isStatusBadge(badge) ? (
            <StatusBadge
              key={
                typeof badge.status === "string"
                  ? badge.status
                  : `status-badge-${index}`
              }
              {...badge}
            />
          ) : (
            <Badge
              key={
                typeof badge.children === "string"
                  ? badge.children
                  : `badge-${index}`
              }
              {...badge}
            />
          )
        )}
      </div>
      {description ? (
        <HighlightedText
          text={description}
          className={cn("block break-words", tableTextClassNames.description)}
        />
      ) : null}
    </div>
  )
}

export type TableCellSwitchProps = ComponentProps<typeof Switch>

export function TableCellSwitch({ className, ...props }: TableCellSwitchProps) {
  return (
    <div className="flex items-center">
      <Switch className={cn(className)} {...props} />
    </div>
  )
}

export type TableActionItem = {
  disabled?: boolean
  href?: string
  icon: ReactNode
  key?: string
  label: string
  onClick?: () => void
  target?: string
  title?: string
  destructive?: boolean
}

type RenderTableActionButtonProps = {
  action: TableActionItem
  iconOnly?: boolean
}

function RenderTableActionButton({
  action,
  iconOnly = false,
}: RenderTableActionButtonProps) {
  const variant = action.destructive ? "destructive" : "outline"

  if (action.href && !action.disabled) {
    return (
      <Button
        asChild
        variant={variant}
        size={iconOnly ? "icon" : "default"}
        leftIcon={!iconOnly ? action.icon : undefined}
        className="rounded-md"
        disabled={action.disabled}
        aria-label={iconOnly ? action.label : undefined}
        title={action.title ?? action.label}
      >
        <a
          href={action.href}
          target={action.target}
          rel={action.target === "_blank" ? "noopener noreferrer" : undefined}
        >
          {iconOnly ? action.icon : action.label}
        </a>
      </Button>
    )
  }

  return (
    <Button
      type="button"
      variant={variant}
      size={iconOnly ? "icon" : "default"}
      onClick={action.href ? undefined : action.onClick}
      disabled={action.disabled}
      leftIcon={!iconOnly ? action.icon : undefined}
      className="rounded-md"
      aria-label={iconOnly ? action.label : undefined}
      title={action.title ?? action.label}
    >
      {iconOnly ? action.icon : action.label}
    </Button>
  )
}

export type TableActionBarProps = {
  className?: string
  maxSecondaryActions?: number
  overflowActions?: TableActionItem[]
  overflowLabel?: string
  primaryAction?: TableActionItem | null
  secondaryActions?: TableActionItem[]
}

export function TableActionBar({
  className,
  maxSecondaryActions = 2,
  overflowActions = [],
  overflowLabel = "More actions",
  primaryAction,
  secondaryActions = [],
}: TableActionBarProps) {
  const visibleSecondaryActions = secondaryActions.slice(0, maxSecondaryActions)
  const overflow = [
    ...secondaryActions.slice(maxSecondaryActions),
    ...overflowActions,
  ]

  return (
    <div
      className={cn(
        "flex w-full flex-wrap items-center justify-end gap-2 sm:flex-nowrap",
        className
      )}
    >
      {primaryAction ? (
        <RenderTableActionButton action={primaryAction} />
      ) : null}
      {visibleSecondaryActions.map((action, index) => (
        <RenderTableActionButton
          key={action.key ?? `${action.label}-${index}`}
          action={action}
          iconOnly
        />
      ))}
      {overflow.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="rounded-md"
              aria-label={overflowLabel}
              title={overflowLabel}
            >
              <EllipsisVertical aria-hidden="true" className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {overflow.map((action, index) =>
              action.href && !action.disabled ? (
                <DropdownMenuItem
                  asChild
                  key={action.key ?? `${action.label}-${index}`}
                  disabled={action.disabled}
                  variant={action.destructive ? "destructive" : "default"}
                >
                  <a
                    href={action.href}
                    target={action.target}
                    rel={
                      action.target === "_blank"
                        ? "noopener noreferrer"
                        : undefined
                    }
                  >
                    {action.icon}
                    {action.label}
                  </a>
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  key={action.key ?? `${action.label}-${index}`}
                  onClick={action.href ? undefined : action.onClick}
                  disabled={action.disabled}
                  variant={action.destructive ? "destructive" : "default"}
                >
                  {action.icon}
                  {action.label}
                </DropdownMenuItem>
              )
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  )
}

export { tableTextClassNames, wrapperTypographyClassNames }
